import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { MapPin, SlidersHorizontal, BadgeCheck, ArrowRight, Clock, Sparkles, PiggyBank, ShieldCheck, Calculator } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, eur, errMsg } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Loader, Stars, Empty, Chip, InsuranceBadge } from "@/components/common";
import { usePrices } from "./Pricing";
const EXTRAS = ["dryer", "folding", "ironing", "pickup", "return", "softener", "perfume", "sensitive"];
const EXTRA_LABELS = { dryer: "Trockner", folding: "Falten", ironing: "Bügeln", pickup: "Abholung", return: "Rücklieferung", softener: "Weichspüler", perfume: "Wäscheparfum", sensitive: "Sensitiv-Waschmittel" };
const ATTRS = ["smoke_free", "pet_free", "eco_detergent", "allergy_friendly", "steam_ironing", "express", "flexible_times"];
const ATTR_LABELS = { smoke_free: "Rauchfreier Haushalt", pet_free: "Haustierfreier Haushalt", eco_detergent: "Öko-/Bio-Waschmittel", allergy_friendly: "Allergikerfreundlich", steam_ironing: "Dampfbügeln", express: "Express (24 h)", flexible_times: "Flexible Zeiten" };
const DAYS_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const CITY_PLZ = { Berlin: "10437", Hamburg: "20095", München: "80331", Köln: "50667", Frankfurt: "60311" };

function ChipToggle({ active, onClick, children, testid }) {
  return (
    <button type="button" aria-pressed={active} onClick={onClick} data-testid={testid}
      className={`w-chip border min-h-[38px] transition-colors ${active ? "bg-mint text-mint-on border-mint" : "bg-white border-line hover:border-gray-300"}`}>
      {active ? "✓ " : ""}{children}
    </button>
  );
}

function Filters({ f, setF, base }) {
  const { t } = useT();
  const toggle = (key, val) => setF({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] });
  return (
    <aside className="w-card p-5 space-y-5 h-fit lg:sticky lg:top-20" data-testid="search-filters">
      <h2 className="font-bold flex items-center gap-2"><SlidersHorizontal size={18} aria-hidden="true" /> {t("search.filters")}</h2>
      <div className="rounded-2xl bg-mint-soft border border-mint p-3 text-sm font-semibold text-mint-ink" data-testid="fixed-price-note">Einheitspreis: {eur(base)} je kg bei allen Waschhelden.</div>
      <button type="button" aria-pressed={f.premium_only} onClick={() => setF({ ...f, premium_only: !f.premium_only })} data-testid="filter-premium-only"
        className={`w-full rounded-2xl border p-3 flex items-center gap-2 text-sm font-bold transition-colors ${f.premium_only ? "bg-mint-ink text-white border-mint-ink" : "bg-white border-line hover:border-mint"}`}>
        <ShieldCheck size={18} aria-hidden="true" /> Nur Premium · Wäschedeckung bestätigt
      </button>
      <div>
        <span className="w-label">Zusatzleistungen</span>
        <div className="flex flex-wrap gap-2">
          {EXTRAS.map((e) => <ChipToggle key={e} active={f.extras.includes(e)} onClick={() => toggle("extras", e)} testid={`filter-extra-${e}`}>{EXTRA_LABELS[e]}</ChipToggle>)}
        </div>
      </div>
      <div>
        <span className="w-label">Haushalt & Besonderheiten</span>
        <div className="flex flex-wrap gap-2">
          {ATTRS.map((a) => <ChipToggle key={a} active={f.attrs.includes(a)} onClick={() => toggle("attrs", a)} testid={`filter-attr-${a}`}>{ATTR_LABELS[a]}</ChipToggle>)}
        </div>
      </div>
      <label className="block"><span className="w-label">{t("search.radius")}: {f.radius} km</span>
        <input type="range" min="2" max="100" value={f.radius} onChange={(e) => setF({ ...f, radius: +e.target.value })} className="w-full accent-[var(--w-mint)]" data-testid="filter-radius" />
      </label>
      <label className="block"><span className="w-label">{t("search.minrating")}</span>
        <select className="w-input" value={f.min_rating} onChange={(e) => setF({ ...f, min_rating: e.target.value })} data-testid="filter-min-rating">
          {[0, 3, 4, 4.5].map((r) => <option key={r} value={r}>{r ? `${r}+` : t("search.any")}</option>)}
        </select>
      </label>
      <label className="block"><span className="w-label">{t("search.weekday")}</span>
        <select className="w-input" value={f.weekday} onChange={(e) => setF({ ...f, weekday: e.target.value })} data-testid="filter-weekday">
          <option value="">{t("search.any")}</option>
          {DAYS_DE.map((d, i) => <option key={d} value={i}>{d}</option>)}
        </select>
      </label>
    </aside>
  );
}

function ProviderCard({ p, i, compact, base }) {
  const { t } = useT();
  const s = p.services || {};
  const at = p.attributes || {};
  const offered = EXTRAS.filter((e) => s[e]).slice(0, 4);
  const traits = ATTRS.filter((a) => at[a]).slice(0, 3);
  const premium = p.insurance?.tier === 2;
  return (
    <Link to={`/p/${p.id}`} className={`w-card w-hover-lift p-5 flex flex-col gap-3 w-rise relative ${premium ? "!border-mint-ink ring-2 ring-[var(--w-mint)] ring-offset-2" : ""}`} style={{ animationDelay: `${i * 50}ms` }} data-testid={`provider-card-${p.id}`}>
      {premium && <span className="absolute -top-2.5 left-5 rounded-full bg-mint-ink text-white text-[11px] font-extrabold px-3 py-1 flex items-center gap-1 shadow-sm" data-testid={`card-premium-ribbon-${p.id}`}><ShieldCheck size={13} aria-hidden="true" /> Premium empfohlen</span>}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {p.avatar_url
            ? <img src={`${process.env.REACT_APP_BACKEND_URL}${p.avatar_url}`} alt={p.display_name} className="h-12 w-12 rounded-2xl object-cover" data-testid={`card-avatar-${p.id}`} />
            : <span className="h-12 w-12 rounded-2xl bg-mint text-mint-on grid place-items-center font-extrabold text-lg" aria-hidden="true">{p.display_name[0]}</span>}
          <div>
            <h3 className="font-bold text-lg leading-tight">{p.display_name}</h3>
            <p className="w-muted flex items-center gap-1 text-sm"><MapPin size={14} aria-hidden="true" /> {p.distance_km != null ? `${t("search.approx")} ${p.distance_km} km · ` : ""}{p.city}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="w-muted text-sm">Festpreis</p>
          <p className="font-extrabold text-xl tabular-nums text-mint-ink">{eur(base)}<span className="text-base font-semibold w-muted"> {t("search.perkg")}</span></p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Stars value={p.rating} count={p.review_count} />
        {p.verified && <Chip tone="mint"><BadgeCheck size={14} aria-hidden="true" /> KYC</Chip>}
        {p.insurance?.tier > 0 && <InsuranceBadge insurance={p.insurance} showDetails={false} testId={`card-insurance-${p.id}`} />}
        {!compact && p.tier !== "standard" && <Chip tone="info">{p.tier.toUpperCase()}</Chip>}
      </div>
      {!compact && (offered.length > 0 || traits.length > 0) && (
        <div className="flex flex-wrap gap-1.5 pt-1 border-t border-line">
          {offered.map((e) => <Chip key={e}>{EXTRA_LABELS[e]}</Chip>)}
          {traits.map((a) => <Chip key={a} tone="info">{ATTR_LABELS[a]}</Chip>)}
        </div>
      )}
    </Link>
  );
}

function makePin(dark) {
  return L.divIcon({
    className: "",
    html: `<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;background:${dark ? "#022c22" : "#19e6b3"};border:3px solid #fff;transform:rotate(-45deg);box-shadow:0 6px 14px rgba(2,44,34,.35)"><div style="width:9px;height:9px;border-radius:50%;background:#fff;position:absolute;top:8px;left:8px"></div></div>`,
    iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -28],
  });
}

function ResultMap({ res, base }) {
  const nav = useNavigate();
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el._leaflet_id) return; // guard against StrictMode double-init
    const map = L.map(el, { scrollWheelZoom: true, zoomControl: true, fadeAnimation: false, zoomAnimation: false, attributionControl: true })
      .setView([res.origin.lat, res.origin.lon], 13, { animate: false });
    L.tileLayer("https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png", {
      maxZoom: 20, subdomains: "ab",
      attribution: '&copy; OpenStreetMap-Mitwirkende · Tiles: HOT, OSM France',
    }).addTo(map);
    L.marker([res.origin.lat, res.origin.lon], { icon: makePin(true) }).addTo(map).bindPopup(`Dein Standort (${res.origin.plz})`);
    const pts = [[res.origin.lat, res.origin.lon]];
    res.results.filter((p) => p.approx).forEach((p) => {
      pts.push([p.approx.lat, p.approx.lon]);
      L.circle([p.approx.lat, p.approx.lon], { radius: 600, color: "#19e6b3", fillColor: "#19e6b3", fillOpacity: 0.14, weight: 1.2 }).addTo(map);
      const m = L.marker([p.approx.lat, p.approx.lon], { icon: makePin(false) }).addTo(map);
      m.bindPopup(`<strong>${p.display_name}</strong><br/>${eur(base)}/kg · ca. ${p.distance_km} km<br/><a href="/p/${p.id}" class="underline font-semibold" style="color:#047857">Profil ansehen</a>`);
      m.on("dblclick", () => nav(`/p/${p.id}`));
    });
    if (pts.length > 1) map.fitBounds(pts, { padding: [50, 50], maxZoom: 14, animate: false });
    const id = setTimeout(() => { try { if (map._container) map.invalidateSize(false); } catch { /* map gone */ } }, 120);
    return () => { clearTimeout(id); try { map.remove(); } catch { /* already removed */ } };
  }, [res, nav]);
  return <div ref={ref} className="rounded-[24px] overflow-hidden border border-line shadow-sm z-0" style={{ height: 360 }} data-testid="search-map" />;
}

const benefitsList = (base, ironFlat) => [
  [Clock, "Wochenende zurück", "Ein 6-kg-Berg kostet dich ca. 3 Stunden. Deine Waschperson: einen Code-Scan."],
  [PiggyBank, `${eur(base)}/kg Festpreis`, "Überall gleich. Keine Überraschungen, kein Preis-Vergleichen nötig."],
  [ShieldCheck, "Geprüfte Versicherungsnachweise", "Washly prüft die Haftpflicht-Nachweise der Waschhelden. Du siehst im Profil, was vorliegt."],
  [Sparkles, `Bügel-Pauschale ${eur(ironFlat)}`, "Egal wie viele Hemden. Einmal zahlen, alles glatt."],
];

function EmptyState({ setParams, setPlz, px }) {
  const [feat, setFeat] = useState(null);
  const base = px?.base ?? 2.99;
  const BENEFITS = benefitsList(base, px?.ironFlat ?? 20);
  useEffect(() => { api.get("/providers/featured").then((r) => setFeat(r.data)).catch(() => setFeat({ providers: [], cities: [] })); }, []);
  const go = (plz) => { setPlz(plz); setParams({ plz }); };
  return (
    <div className="space-y-10">
      <section className="relative overflow-hidden rounded-[28px] bg-mint text-mint-on p-6 md:p-10 w-rise">
        <div className="w-blob w-blob-a" aria-hidden="true" />
        <div className="relative grid md:grid-cols-2 gap-6 items-center">
          <div>
            <p className="text-sm font-bold uppercase tracking-wider opacity-70 mb-2">Wo soll's sauber werden?</p>
            <h1 className="text-3xl md:text-4xl font-extrabold leading-tight mb-3 w-headline">Gib deine PLZ ein – oder spring direkt in eine Stadt.</h1>
            <p className="opacity-80">Wir zeigen dir geprüfte Waschhelden in der Nähe – zum Einheitspreis von {eur(base)} je kg, mit freien Zeitfenstern.</p>
          </div>
          <div className="flex flex-wrap gap-2 md:justify-end" data-testid="city-chips">
            {Object.entries(CITY_PLZ).map(([city, plz]) => {
              const count = feat?.cities?.find((c) => c.city === city)?.count;
              return (
                <button key={city} type="button" onClick={() => go(plz)} data-testid={`city-chip-${city}`}
                  className="rounded-full bg-mint-on text-white px-5 py-3 font-semibold hover:bg-[#053d2f] transition-colors flex items-center gap-2">
                  <MapPin size={16} aria-hidden="true" /> {city}{count ? <span className="rounded-full bg-mint text-mint-on text-xs px-2 py-0.5 font-bold">{count}</span> : null}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" aria-label="Vorteile">
        {BENEFITS.map(([Icon, title, d], i) => (
          <div key={title} className="w-card w-hover-lift p-5 w-rise" style={{ animationDelay: `${120 + i * 60}ms` }}>
            <span className="h-11 w-11 rounded-2xl grid place-items-center mb-3" style={{ background: ["#19e6b3", "#ffd166", "#ff6b6b", "#7c5cff"][i], color: i === 3 ? "#fff" : "#022c22" }}><Icon size={20} aria-hidden="true" /></span>
            <h3 className="font-bold">{title}</h3><p className="w-muted text-sm mt-1">{d}</p>
          </div>
        ))}
      </section>

      <section>
        <div className="flex items-end justify-between gap-4 mb-4">
          <div><p className="w-label !text-mint-ink !mb-1">Beliebt</p><h2 className="text-2xl font-extrabold">Diese Waschhelden sind gerade gefragt</h2></div>
          <Link to="/preise" className="hidden sm:inline-flex w-btn w-btn-secondary" data-testid="empty-pricing-link"><Calculator size={18} aria-hidden="true" /> Preis berechnen</Link>
        </div>
        {!feat ? <Loader /> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="featured-providers">{feat.providers.map((p, i) => <ProviderCard key={p.id} p={p} i={i} compact base={base} />)}</div>
        )}
      </section>

      <section className="rounded-[28px] bg-mint-on text-white p-6 md:p-10 flex flex-wrap items-center justify-between gap-6 relative overflow-hidden">
        <div className="w-blob w-blob-c" aria-hidden="true" />
        <div className="relative"><h2 className="text-2xl md:text-3xl font-extrabold mb-1">Deine Maschine hat Langeweile?</h2><p className="text-white/75">Werde Waschheld und verdiene mit jedem Waschgang, der sowieso läuft.</p></div>
        <Link to="/waschheld" className="w-btn w-btn-primary relative" data-testid="empty-hero-link">So geht's <ArrowRight size={18} aria-hidden="true" /></Link>
      </section>
    </div>
  );
}

export default function SearchPage() {
  const { t } = useT();
  const px = usePrices();
  const base = px?.base ?? 2.99;
  const [params, setParams] = useSearchParams();
  const [plz, setPlz] = useState(params.get("plz") || "");
  const [f, setF] = useState({ extras: [], attrs: [], radius: 25, min_rating: 0, weekday: "", premium_only: false });
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  const q = params.get("plz");
  const hasCalcDraft = !!sessionStorage.getItem("washly_draft_calc");

  useEffect(() => {
    if (!q) return;
    setRes(null);
    setError("");
    const p = { plz: q, radius: f.radius, extras: f.extras.join(","), attrs: f.attrs.join(","), min_rating: f.min_rating };
    if (f.weekday !== "") p.weekday = f.weekday;
    if (f.premium_only) p.premium_only = true;
    api.get("/providers/search", { params: p }).then((r) => setRes(r.data)).catch((e) => setError(errMsg(e)));
  }, [q, f]);

  return (
    <div className="w-container py-8">
      <form className="flex gap-3 mb-6 max-w-lg" onSubmit={(e) => { e.preventDefault(); if (/^\d{5}$/.test(plz)) setParams({ plz }); }}>
        <input className="w-input !rounded-full" inputMode="numeric" maxLength={5} aria-label={t("hero.plz")} placeholder={t("hero.plz")} value={plz} onChange={(e) => setPlz(e.target.value.replace(/\D/g, ""))} data-testid="search-plz-input" />
        <button className="w-btn w-btn-primary" data-testid="search-submit-btn">{t("nav.search")}</button>
      </form>
      {hasCalcDraft && <p className="w-card p-3 mb-4 bg-mint-soft border-mint font-semibold flex items-center gap-2" data-testid="calc-draft-banner"><Calculator size={18} className="text-mint-ink" aria-hidden="true" /> Deine Auswahl aus dem Preisrechner wird bei der Buchung übernommen.</p>}
      {!q ? <EmptyState setParams={setParams} setPlz={setPlz} px={px} /> : (
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-3"><Filters f={f} setF={setF} base={base} /></div>
          <div className="lg:col-span-9 space-y-4">
            {res && <h1 className="text-2xl font-extrabold" data-testid="search-heading">{t("search.title")} {res.origin.plz} {res.origin.place} <span className="w-muted font-semibold">· {res.results.length}</span></h1>}
            {res && (
              <details className="w-card p-4 text-sm" data-testid="ranking-info">
                <summary className="cursor-pointer font-semibold select-none">Wie sortiert Washly die Ergebnisse?</summary>
                <div className="w-muted mt-3 space-y-1">
                  <p>Die Reihenfolge entsteht aus folgenden Kriterien – unabhängig von Zahlungen für eine bessere Platzierung:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Anbieter mit bestätigter Premium-Wäschedeckung werden zuerst angezeigt.</li>
                    <li>Entfernung zu deiner Postleitzahl (näher zuerst).</li>
                    <li>Durchschnittliche Bewertung aus abgeschlossenen Aufträgen.</li>
                  </ul>
                  <p>Der Waschpreis ist bei allen Anbietern identisch und beeinflusst die Sortierung nicht.</p>
                </div>
              </details>
            )}
            {error && <p role="alert" className="text-[var(--w-error)] font-semibold" data-testid="search-error">{error}</p>}
            {!res && !error && <Loader />}
            {res && res.origin.lat != null && <ResultMap res={res} base={base} />}
            {res && !res.results.length && <Empty>{t("search.none")}</Empty>}
            <div className="grid md:grid-cols-2 gap-4" data-testid="search-results">{res?.results.map((p, i) => <ProviderCard key={p.id} p={p} i={i} base={base} />)}</div>
          </div>
        </div>
      )}
    </div>
  );
}
